/**
 * Live shard-level progress for one external test run.
 *
 * @module features/external-tests/useExternalRunStream
 * @description Subscribes to `GET /jobs/{runId}/stream` with the RUN id (a run
 * is N+1 jobs, each rescheduled repeatedly, so no single job id survives the
 * run) and folds the events into a small render-ready shape: the plan, and one
 * entry per shard.
 *
 * **This is not the source of truth for results.** `useExternalRun`'s 3s poll
 * remains authoritative for the item table and the run's final counts; the
 * stream answers a different question — "what is happening right now" — and
 * doubles as the poll's early nudge. Two sources for two questions, not two
 * sources for one: a client that summed per-event counts would drift
 * permanently the first time SSE dropped an event, which SSE makes routine.
 */
import React from 'react';
import { EXTERNAL_RUN_EVENT_TYPES, type ExternalRunEvent } from '@chatsdet/types';
import { API_URL } from '../../config';
import type {
  ExternalRunStreamProgressState,
  ExternalShardStreamProgress,
} from './externalRunProgressModel';

const MAX_RECONNECT_ATTEMPTS = 5;

/** SSE transport state uses the shared model shape but never supplies launchState. */
export type ExternalRunStreamState = ExternalRunStreamProgressState;

const EMPTY: ExternalRunStreamState = {
  status: null,
  shardTotal: null,
  revision: null,
  targetUrl: null,
  shards: [],
  sealed: null,
  isLive: false,
};

/** Fold one event into the accumulated state. Pure, so it is trivially testable. */
export function applyExternalRunEvent(
  prev: ExternalRunStreamState,
  event: ExternalRunEvent
): ExternalRunStreamState {
  switch (event.type) {
    case 'external-run.status':
      return { ...prev, status: event.data.status, isLive: true };
    case 'external-run.phase':
      return {
        ...prev,
        shardTotal: event.data.shardTotal,
        revision: event.data.revision,
        targetUrl: event.data.targetUrl,
        isLive: true,
      };
    case 'external-run.shard': {
      const shards = [...prev.shards];
      const at = shards.findIndex((s) => s.shardIndex === event.data.shardIndex);
      // Keep whatever `hadResults` we already learned: the results event and the
      // phase event are independent, and a later phase must not erase it.
      const existing = at >= 0 ? shards[at] : null;
      const next: ExternalShardStreamProgress = {
        shardIndex: event.data.shardIndex,
        phase: event.data.phase,
        attempt: event.data.attempt,
        hadResults: existing?.hadResults ?? false,
      };
      if (at >= 0) shards[at] = next;
      else shards.push(next);
      shards.sort((a, b) => a.shardIndex - b.shardIndex);
      return {
        ...prev,
        shards,
        // The plan event normally arrives first, but a reconnect replays from a
        // cursor that may have dropped it — so trust the shard's own total too.
        shardTotal: prev.shardTotal ?? event.data.shardTotal,
        isLive: true,
      };
    }
    case 'external-run.results': {
      if (!event.data.hadResults) return { ...prev, isLive: true };
      const shards = prev.shards.map((s) =>
        s.shardIndex === event.data.shardIndex ? { ...s, hadResults: true } : s
      );
      return { ...prev, shards, isLive: true };
    }
    case 'external-run.sealed':
      return {
        ...prev,
        status: 'sealed',
        sealed: {
          status: event.data.status,
          total: event.data.total,
          passed: event.data.passed,
          failed: event.data.failed,
        },
        isLive: true,
      };
  }
}

/**
 * Subscribe to a run's progress stream.
 *
 * @param runId the RUN id (not a job id); pass null to stay disconnected.
 * @param onResults called when a shard reports results, so the caller can nudge
 *   its poll instead of waiting out the next 3s tick. The stream never carries
 *   the results themselves — see the module note.
 */
export function useExternalRunStream(
  runId: string | null,
  onResults?: () => void
): ExternalRunStreamState {
  const [state, setState] = React.useState<ExternalRunStreamState>(EMPTY);
  // Held in a ref so a caller passing an inline arrow does not tear down and
  // rebuild the EventSource on every render.
  const onResultsRef = React.useRef(onResults);
  onResultsRef.current = onResults;

  React.useEffect(() => {
    if (!runId) {
      setState(EMPTY);
      return;
    }

    setState(EMPTY);
    let cancelled = false;
    let source: EventSource | null = null;
    let reconnectTimer: number | null = null;
    let attempts = 0;
    // Replay cursor: on reconnect the server re-sends everything newer than
    // this, so a dropped connection costs nothing but a little duplication
    // (every fold above is idempotent).
    let since: string | null = null;

    const connect = () => {
      if (cancelled) return;
      const url = new URL(`${API_URL}/jobs/${runId}/stream`);
      if (since) url.searchParams.set('since', since);
      const es = new EventSource(url.toString(), { withCredentials: true });
      source = es;

      es.onopen = () => {
        attempts = 0;
      };

      const handle = (raw: MessageEvent, type: ExternalRunEvent['type']) => {
        try {
          // The SSE frame carries the discriminator in its `event:` line and the
          // payload in `data:`, so the correlation TypeScript needs has to be
          // re-established here. Asserted once, at the single parse point, from
          // the listener that was registered for exactly this `type`.
          const event = { type, data: JSON.parse(raw.data) } as ExternalRunEvent;
          if (typeof event.data.emittedAt === 'string') since = event.data.emittedAt;
          setState((prev) => applyExternalRunEvent(prev, event));
          if (event.type === 'external-run.results' && event.data.hadResults) {
            onResultsRef.current?.();
          }
          if (event.type === 'external-run.sealed') onResultsRef.current?.();
        } catch (error) {
          console.error('Failed to parse external run event', error);
        }
      };

      for (const type of EXTERNAL_RUN_EVENT_TYPES) {
        es.addEventListener(type, ((raw: MessageEvent) => handle(raw, type)) as EventListener);
      }

      es.onerror = () => {
        es.close();
        if (cancelled) return;
        attempts += 1;
        // Give up quietly rather than reconnecting forever. Losing the stream
        // costs live shard chips and nothing else — the poll still finishes the
        // page — so a permanent retry loop would be pure noise.
        if (attempts > MAX_RECONNECT_ATTEMPTS) return;
        const backoffMs = Math.min(10_000, 1000 * 2 ** (attempts - 1));
        reconnectTimer = window.setTimeout(connect, backoffMs);
      };
    };

    connect();

    return () => {
      cancelled = true;
      source?.close();
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    };
  }, [runId]);

  return state;
}
